import { useCallback, useMemo, useRef, useState } from 'react';
import { isInvokeCapabilityMsg, messages } from '../../../common/messages';
import { Tags } from '../../../config';
import { useComponentFunctionsManager } from './useComponentFunctionsManager';
import { useComponentPropsManager } from './useComponentPropsManager';
import { useComponentStateManager } from './useComponentStateManager';
import { useSrcManager } from './useSrcManager';
import { getPortalId } from '../../../common/utils';
import { useSetupMagpie } from './useSetupMagpie';
const getComponentNamesByTags = appliedConfig => {
  const tagToNamesMap = Object.values(Tags).reduce((map, tag) => {
    map[tag] = [];
    return map;
  }, {});
  for (const [componentName, {
    tags = []
  }] of Object.entries(appliedConfig)) {
    for (const tag of tags) {
      tagToNamesMap[tag].push(componentName);
    }
  }
  return tagToNamesMap;
};
export const useCreateNavComponentsManager = ({
  config,
  params,
  metricsStartTime,
  enableScheduler,
  platformName
}) => {
  const apiContext = useRef({
    configSpec: config.spec,
    appliedConfig: config(params),
    params,
    iframeContextMap: {},
    portalId: getPortalId(),
    enableScheduler,
    platformName
  });
  useSetupMagpie(platformName);
  const componentNamesByTag = useMemo(() => getComponentNamesByTags(apiContext.current.appliedConfig), []);
  const [subscribersMap, setSubscribersMap] = useState({});
  const {
    getComponentProps,
    setComponentProps
  } = useComponentPropsManager(apiContext);
  const componentStateManager = useComponentStateManager(apiContext);
  const componentFunctionsManager = useComponentFunctionsManager(apiContext, componentStateManager.waitUntilReady);
  const {
    setComponentState
  } = componentStateManager;
  const handleInvokeCapability = useCallback(({
    payload: {
      name,
      args,
      source
    }
  }) => {
    const {
      [name]: subscribers = []
    } = subscribersMap;
    subscribers.forEach(subscriber => subscriber(args, {
      source
    }));
  }, [subscribersMap]);
  const handleMessage = useCallback(msg => {
    if (isInvokeCapabilityMsg(msg)) {
      handleInvokeCapability(msg);
    }
  }, [handleInvokeCapability]);
  const subscribe = useCallback((name, callback) => {
    setSubscribersMap(map => {
      const currentSubscribers = map[name];
      const updatedSubscribers = Array.isArray(currentSubscribers) ? [...currentSubscribers, callback] : [callback];
      return Object.assign({}, map, {
        [name]: updatedSubscribers
      });
    });
    return function unsubscribe() {
      setSubscribersMap(map => {
        const currentSubscribers = map[name];
        if (!Array.isArray(currentSubscribers)) {
          return map;
        }
        const updatedSubscribers = currentSubscribers.filter(cb => cb !== callback);
        return Object.assign({}, map, {
          [name]: updatedSubscribers
        });
      });
    };
  }, []);
  const handleReady = useCallback((name, ctx) => {
    apiContext.current.iframeContextMap = Object.assign({}, apiContext.current.iframeContextMap, {
      [name]: ctx
    });
    const componentDef = apiContext.current.configSpec[name];
    // Initialize component state AND send a message
    setComponentState(name, currentComponentState => Object.assign({}, currentComponentState, {
      isReady: true,
      isLoaded: true
    }));
    // Initialize component props - take into account that before component was ready props could've changed
    const currentComponentProps = getComponentProps(componentDef);
    ctx.sendMessage(messages.SET_COMPONENT_PROPS, {
      props: currentComponentProps
    });
  }, [getComponentProps, setComponentState]);
  const getComponentSrc = useSrcManager();
  return useMemo(() => Object.assign({
    config: apiContext.current.appliedConfig,
    handleMessage,
    handleReady,
    subscribe,
    getComponentProps,
    setComponentProps,
    metricsStartTime,
    componentNamesByTag,
    getComponentSrc,
    configSpec: Object.freeze(config.spec)
  }, componentStateManager, componentFunctionsManager), [handleMessage, handleReady, subscribe, getComponentProps, setComponentProps, metricsStartTime, componentNamesByTag, getComponentSrc, config.spec, componentStateManager, componentFunctionsManager]);
};